import { memo, useState, useCallback, useMemo } from 'react'
import { Settings, Check, Loader2, ChevronDown, ExternalLink } from 'lucide-react'
import { AnimatePresence, motion, Variants, Transition } from 'framer-motion'

interface CustomInstructionsResult {
  success?: boolean
  message?: string
  instructions?: {
    nickname?: string
    occupation?: string
    writingStyle?: string
    thingsToRemember?: string
  }
  error?: string
}

interface CustomInstructionsToolProps {
  result: CustomInstructionsResult | null
  input: Record<string, string> | string | null
  isLoading: boolean
  error?: string | null
}

const FIELD_LABELS: Record<string, string> = {
  nickname: 'Name',
  occupation: 'Role',
  writingStyle: 'Writing Style',
  thingsToRemember: 'Things to Remember',
}

function parseInput(input: Record<string, string> | string | null): Record<string, string> {
  if (!input) return {}
  if (typeof input === 'string') {
    try {
      return JSON.parse(input)
    } catch {
      return {}
    }
  }
  return input
}

const detailsVariants: Variants = {
  collapsed: { height: 0, opacity: 0 },
  expanded: { height: 'auto', opacity: 1 },
}

const detailsTransition: Transition = {
  height: { duration: 0.2, ease: 'easeInOut' },
  opacity: { duration: 0.15, ease: 'easeInOut' },
}

const chevronVariants: Variants = {
  collapsed: { rotate: 0 },
  expanded: { rotate: 180 },
}

const chevronTransition: Transition = { duration: 0.2, ease: 'easeInOut' }

export const CustomInstructionsTool = memo(({ result, input, isLoading, error }: CustomInstructionsToolProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const toggleExpanded = useCallback(() => setIsExpanded((prev) => !prev), [])

  const openSettings = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    window.open('highlight://settings/account', '_blank')
  }, [])

  const parsedInput = useMemo(() => parseInput(input), [input])

  const updatedEntries = useMemo(
    () =>
      Object.entries(parsedInput).filter(([key, value]) => value !== undefined && value !== '' && key in FIELD_LABELS),
    [parsedInput],
  )

  if (isLoading) {
    return (
      <div className="bg-background-subtle/50 flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-sm">
        <Loader2 className="text-brand-9 size-4 animate-spin" />
        <span className="text-foreground-secondary">Updating your preferences...</span>
      </div>
    )
  }

  if (error || result?.error) {
    return (
      <div className="bg-danger/5 border-danger flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-sm">
        <Settings className="text-danger-subtle/70 size-4" />
        <span className="text-danger-subtle/80">Failed to update preferences</span>
      </div>
    )
  }

  if (!result?.success) return null
  if (updatedEntries.length === 0) return null

  return (
    <div
      className="bg-brand/5 border-brand/15 hover:bg-brand-subtle cursor-pointer rounded-xl border text-sm transition-colors"
      onClick={toggleExpanded}
    >
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <div className="bg-brand/15 flex size-5 shrink-0 items-center justify-center rounded-full">
          <Check className="text-brand-9 size-3" strokeWidth={2.5} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-foreground-secondary font-medium">Preferences updated</span>
          <span className="text-foreground-placeholder text-xs">
            {updatedEntries.map(([f]) => FIELD_LABELS[f] ?? f).join(', ')}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            onClick={openSettings}
            className="text-foreground-placeholder hover:text-foreground-secondary bg-element-hover hover:bg-element-active flex items-center gap-1 rounded-md px-1.5 py-1 text-xs transition-colors"
          >
            <ExternalLink className="size-3" />
            <span>View</span>
          </button>
          <motion.div
            variants={chevronVariants}
            animate={isExpanded ? 'expanded' : 'collapsed'}
            transition={chevronTransition}
          >
            <ChevronDown className="text-foreground-placeholder size-4" />
          </motion.div>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            variants={detailsVariants}
            initial="collapsed"
            animate="expanded"
            exit="collapsed"
            transition={detailsTransition}
            className="overflow-hidden"
          >
            <div className="border-brand/10 mx-3 mb-2.5 flex flex-col gap-2 border-t pt-2">
              {updatedEntries.map(([key, value]) => (
                <div key={key} className="flex flex-col gap-0.5">
                  <span className="text-foreground-placeholder text-xs font-medium uppercase">
                    {FIELD_LABELS[key] ?? key}
                  </span>
                  <span className="text-foreground-secondary whitespace-pre-wrap text-sm">{String(value)}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
})

CustomInstructionsTool.displayName = 'CustomInstructionsTool'
